import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  offerStandaloneService,
  getStandaloneOfferUrlSegment,
  type OfferStandalone,
} from '../../services/offerStandaloneService';
import {
  createEstimate,
  getEstimate,
  getEstimateList,
  putEstimateEasyPrzedmiar,
  type EstimateHeader,
  type EstimateFull,
} from '../../services/estimateService';
import { compareSectionNr, parseRange, type TesSection } from '../../utils/sectionUtils';
import { useNotification } from '../layouts/Layout';

interface EstimateExpense {
  id: number;
  type: 'R' | 'M' | 'S' | 'Kp' | 'Kz' | 'Z';
  description: string;
  unit: string;
  norm: number;
  expenses: number;
  rmsPrice: number;
}

interface ExportPosition {
  id: number;
  nr: string;
  basis?: string;
  description: string;
  quantity: number;
  unit: string;
  positionType: 'detailed' | 'simplified' | string;
  unitPrice?: number;
  children?: EstimateExpense[];
}

export interface ExportToEasyEstimateDialogProps {
  open: boolean;
  onClose: () => void;
  sourceEstimateId: number;
  sourceEstimateName?: string;
  positions: ExportPosition[];
  tesRows: TesSection[];
  markups: { kp: number; kz: number; z: number };
  onSuccess?: (newEstimateId: number) => void;
}

function offerLabel(offer: OfferStandalone): string {
  const seg = getStandaloneOfferUrlSegment(offer);
  const parts: string[] = [seg];
  if (offer.client_name) parts.push(offer.client_name);
  if (offer.order_name) parts.push(offer.order_name);
  return parts.join(' · ');
}

function estimateLabel(h: EstimateHeader): string {
  const parts: string[] = [];
  if (h.estimate_number) parts.push(h.estimate_number);
  if (h.name) parts.push(h.name);
  if (h.object_name) parts.push(h.object_name);
  return parts.filter(Boolean).join(' · ') || `#${h.id}`;
}

/** pl-PL, przecinek jako separator dziesiętny, spacja jako separator tysięcy (zgodnie z easy-przedmiar). */
function formatPlNumber(n: number, decimals: number): string {
  if (!Number.isFinite(n)) return '';
  const s = n.toLocaleString('pl-PL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  });
  return s.replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ');
}

function computeUnitPrice(p: ExportPosition, markups: { kp: number; kz: number; z: number }): number {
  if (p.positionType === 'simplified') {
    return Number(p.unitPrice) || 0;
  }
  const children = p.children || [];
  const sumR = children.filter(c => c.type === 'R').reduce((s, c) => s + c.norm * c.rmsPrice, 0);
  const sumM = children.filter(c => c.type === 'M').reduce((s, c) => s + c.norm * c.rmsPrice, 0);
  const sumS = children.filter(c => c.type === 'S').reduce((s, c) => s + c.norm * c.rmsPrice, 0);
  const kp = (Number.isFinite(markups.kp) ? markups.kp : 0) / 100 * sumR;
  const kz = (Number.isFinite(markups.kz) ? markups.kz : 0) / 100 * sumM;
  const z = (Number.isFinite(markups.z) ? markups.z : 0) / 100 * (sumR + sumM + sumS + kp + kz);
  return sumR + sumM + sumS + kp + kz + z;
}

/** Buduje wiersze easy-przedmiar (dział + pozycje) z kosztorysu Estimates. Bez nakładów. */
export function buildEasyPrzedmiarRowsFromEstimate(params: {
  positions: ExportPosition[];
  tesRows: TesSection[];
  markups: { kp: number; kz: number; z: number };
}): unknown[] {
  const { positions, tesRows, markups } = params;
  const rows: unknown[] = [];
  const sortedSecs = [...(tesRows || [])].sort((a, b) => compareSectionNr(a.nr, b.nr));
  const assignedIds = new Set<number>();
  let lp = 1;

  const pushPosition = (p: ExportPosition, dzial: string | null) => {
    const unitPrice = computeUnitPrice(p, markups);
    rows.push({
      rowType: 'pozycja',
      lp: String(lp++),
      podstawa: p.basis || '',
      opis: p.description || '',
      jm: p.unit || '',
      ilosc: p.quantity != null ? formatPlNumber(Number(p.quantity), 3) : '',
      uwagi: '',
      pozycjaDzial: dzial,
      extras: {
        cenaJedn: unitPrice > 0 ? formatPlNumber(unitPrice, 2) : '',
      },
    });
  };

  for (const sec of sortedSecs) {
    rows.push({
      rowType: 'dzial',
      dzialTitle: sec.name || '',
      dzialKey: `sec-${sec.id}`,
      dzialOutlineNr: sec.nr || null,
    });

    const rng = parseRange(sec.range);
    if (!rng) continue;
    const [lo, hi] = rng;
    const inSection = positions
      .filter(p => {
        if (assignedIds.has(p.id)) return false;
        const n = parseInt(String(p.nr), 10);
        return !isNaN(n) && n >= lo && n <= hi;
      })
      .sort((a, b) => (parseInt(String(a.nr), 10) || 0) - (parseInt(String(b.nr), 10) || 0));

    for (const p of inSection) {
      assignedIds.add(p.id);
      pushPosition(p, sec.nr || null);
    }
  }

  const unassigned = positions
    .filter(p => !assignedIds.has(p.id))
    .sort((a, b) => (parseInt(String(a.nr), 10) || 0) - (parseInt(String(b.nr), 10) || 0));

  if (unassigned.length > 0) {
    rows.push({
      rowType: 'dzial',
      dzialTitle: 'Pozostałe pozycje',
      dzialKey: 'sec-misc',
      dzialOutlineNr: null,
    });
    for (const p of unassigned) pushPosition(p, null);
  }

  return rows;
}

const ExportToEasyEstimateDialog: React.FC<ExportToEasyEstimateDialogProps> = ({
  open,
  onClose,
  sourceEstimateId,
  sourceEstimateName,
  positions,
  tesRows,
  markups,
  onSuccess,
}) => {
  const [offers, setOffers] = React.useState<OfferStandalone[]>([]);
  const [offersLoading, setOffersLoading] = React.useState(false);
  const [offersError, setOffersError] = React.useState<string | null>(null);
  const [selectedOffer, setSelectedOffer] = React.useState<OfferStandalone | null>(null);

  const [mode, setMode] = React.useState<'new' | 'overwrite'>('new');

  const [estimates, setEstimates] = React.useState<EstimateHeader[]>([]);
  const [estimatesLoading, setEstimatesLoading] = React.useState(false);
  const [selectedEstimate, setSelectedEstimate] = React.useState<EstimateHeader | null>(null);

  const [submitting, setSubmitting] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const { showNotification } = useNotification();

  React.useEffect(() => {
    if (!open) return;
    setSelectedOffer(null);
    setSelectedEstimate(null);
    setEstimates([]);
    setMode('new');
    setErrorMsg(null);
    setOffersLoading(true);
    setOffersError(null);
    offerStandaloneService
      .getAllOffers()
      .then((list) => {
        const sorted = [...list].sort((a, b) => {
          const av = a.offer_sequence_number ?? 0;
          const bv = b.offer_sequence_number ?? 0;
          return bv - av;
        });
        setOffers(sorted);
      })
      .catch(() => setOffersError('Nie udało się wczytać listy ofert.'))
      .finally(() => setOffersLoading(false));
  }, [open]);

  React.useEffect(() => {
    if (!open || !selectedOffer || mode !== 'overwrite') {
      if (mode !== 'overwrite') setSelectedEstimate(null);
      return;
    }
    setEstimatesLoading(true);
    getEstimateList({ standaloneOfferId: selectedOffer.id })
      .then((list) => {
        setEstimates(list);
      })
      .catch(() => setEstimates([]))
      .finally(() => setEstimatesLoading(false));
  }, [open, selectedOffer, mode]);

  const rowsPreview = React.useMemo(
    () => buildEasyPrzedmiarRowsFromEstimate({ positions, tesRows, markups }),
    [positions, tesRows, markups]
  );
  const dzialCount = rowsPreview.filter((r: any) => r?.rowType === 'dzial').length;
  const pozycjaCount = rowsPreview.filter((r: any) => r?.rowType === 'pozycja').length;

  const canSubmit =
    !submitting &&
    !!selectedOffer &&
    (mode === 'new' || (mode === 'overwrite' && !!selectedEstimate)) &&
    rowsPreview.length > 0;

  const handleSubmit = async () => {
    if (!selectedOffer) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const rows = buildEasyPrzedmiarRowsFromEstimate({ positions, tesRows, markups });

      let targetEstimateId: number;

      if (mode === 'overwrite') {
        if (!selectedEstimate) throw new Error('Wybierz kosztorys do nadpisania.');
        targetEstimateId = selectedEstimate.id;
      } else {
        const src: EstimateFull = await getEstimate(sourceEstimateId);
        const today = new Date().toISOString().slice(0, 10);
        const baseName = (sourceEstimateName || src.name || 'Kosztorys ofertowy').trim();
        const newName = baseName.toLowerCase().includes('easy') ? baseName : `${baseName} (EasyEstimate)`;
        const created = await createEstimate({
          name: newName.slice(0, 200),
          object_name: src.object_name || selectedOffer.order_name || 'Obiekt',
          client: src.client || selectedOffer.client_name || 'Zamawiający',
          estimate_location: src.estimate_location || selectedOffer.location || undefined,
          branza: (src.branza || undefined) as string | undefined,
          client_address: src.client_address || selectedOffer.client_address || undefined,
          contractor_name: src.contractor_name || undefined,
          contractor_address: src.contractor_address || undefined,
          preparer: src.preparer || '—',
          reviewer: src.reviewer || undefined,
          markup_kp: Number(src.markup_kp) || 0,
          markup_kz: Number(src.markup_kz) || 0,
          markup_z: Number(src.markup_z) || 0,
          vat: Number(src.vat) || 23,
          rg: src.rg != null ? Number(src.rg) : undefined,
          prepared_at: today,
          standalone_offer_id: selectedOffer.id,
        });
        targetEstimateId = created.id;
      }

      const res = await putEstimateEasyPrzedmiar(targetEstimateId, rows);
      if (!res.success) throw new Error(res.message || 'Nie udało się zapisać przedmiaru.');

      try {
        showNotification(
          mode === 'overwrite'
            ? 'Kosztorys został nadpisany w EasyEstimate.'
            : 'Kosztorys został wyeksportowany do EasyEstimate.',
          'success'
        );
      } catch {
        /* brak kontekstu Layout — zignoruj */
      }

      if (onSuccess) onSuccess(targetEstimateId);
      onClose();
    } catch (err: any) {
      console.error('[ExportToEasyEstimateDialog] submit error', err);
      setErrorMsg(err?.response?.data?.message || err?.message || 'Nie udało się wyeksportować kosztorysu.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500 }}>
        Eksport kosztorysu do EasyEstimate
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: '#5F6368', mb: 2 }}>
          Zostaną przeniesione działy, pozycje (LP, opis, jednostka, ilość) oraz obliczone ceny jednostkowe.
          Nakłady (R/M/S) nie są eksportowane.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Autocomplete
            size="small"
            options={offers}
            loading={offersLoading}
            value={selectedOffer}
            onChange={(_, v) => setSelectedOffer(v)}
            getOptionLabel={offerLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Oferta"
                placeholder="Wybierz ofertę (OF.X.RRRR)"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {offersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {offersError && <Alert severity="error">{offersError}</Alert>}

          <RadioGroup
            value={mode}
            onChange={(_, v) => setMode(v as 'new' | 'overwrite')}
          >
            <FormControlLabel value="new" control={<Radio />} label="Utwórz nowy kosztorys w ofercie" />
            <FormControlLabel value="overwrite" control={<Radio />} label="Nadpisz istniejący kosztorys w ofercie" />
          </RadioGroup>

          {mode === 'overwrite' && (
            <Autocomplete
              size="small"
              options={estimates}
              loading={estimatesLoading}
              value={selectedEstimate}
              onChange={(_, v) => setSelectedEstimate(v)}
              getOptionLabel={estimateLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disabled={!selectedOffer}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Kosztorys do nadpisania"
                  placeholder={!selectedOffer ? 'Najpierw wybierz ofertę' : 'Wybierz kosztorys'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {estimatesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          <Box sx={{ bgcolor: '#F6F6FA', border: '1px solid #EDEDED', borderRadius: '4px', p: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#2D2D32' }}>
              Podgląd eksportu:
            </Typography>
            <Typography variant="body2" sx={{ color: '#5F6368' }}>
              Działy: <b>{dzialCount}</b>, pozycje: <b>{pozycjaCount}</b>
            </Typography>
            {pozycjaCount === 0 && (
              <Typography variant="body2" sx={{ color: '#c62828', mt: 0.5 }}>
                Brak pozycji do eksportu.
              </Typography>
            )}
          </Box>

          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Anuluj
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: '#4040DB',
            '&:hover': { backgroundColor: '#1F1FB0' },
          }}
        >
          {submitting ? 'Eksportuję…' : 'Eksportuj'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportToEasyEstimateDialog;
